<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import AddSparePartsDialog from '@/components/rc/spare-parts/AddSparePartsDialog.vue'
import BulkAddSparePartsDialog from '@/components/rc/spare-parts/BulkAddSparePartsDialog.vue'
import BulkUpdateSparePartsDialog from '@/components/rc/spare-parts/BulkUpdateSparePartsDialog.vue'
import SparePartsTable from '@/components/rc/tables/SparePartsTable.vue'
import { useSpareParts } from '@/composables/rc/spare-parts'
import { useOIDCStore } from '@/stores/oidc.store'
import type { ISparePart } from '@/types/rc/spare-parts'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { ArrowDown } from '@element-plus/icons-vue'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

const oidcStore = useOIDCStore()
const { isCustomer } = storeToRefs(oidcStore)

const isCreationDialogOpen = ref(false)
const isBulkAddSparePartsDialogOpen = ref(false)
const isBulkUpdateSparePartsDialogOpen = ref(false)
const { bulkDeleteSparePartsMutation, bulkArchiveSparePartsMutation } = useSpareParts()

const selection = ref<ISparePart[]>([])

const onSelect = (parts: ISparePart[]) => {
  selection.value = parts
}

const onSelectAll = (parts: ISparePart[]) => {
  selection.value = parts
}

const handleBulkDelete = () => {
  DeleteConfirmationModal(
    'Delete Spare Parts',
    `Are you sure you want to delete ${selection.value.length} spare parts? This action cannot be undone`,
    selection.value.map((part) => part.id),
    bulkDeleteSparePartsMutation.mutateAsync,
    () => {
      selection.value = []
    }
  )
}

const handleBulkArchive = () => {
  DeleteConfirmationModal(
    'Archive Spare Parts',
    `Are you sure you want to archive ${selection.value.length} spare parts? This action cannot be undone`,
    selection.value.map((part) => part.id),
    bulkArchiveSparePartsMutation.mutateAsync,
    () => {
      selection.value = []
    },
    'Archive'
  )
}

const handleBulkUpdateDialogClose = () => {
  isBulkUpdateSparePartsDialogOpen.value = false
  selection.value = []
}
</script>

<template>
  <ViewLayout class="spare-parts">
    <template v-if="!isCustomer" #controls>
      <el-button v-if="selection.length" @click="handleBulkArchive"> Archive </el-button>
      <el-button v-if="selection.length" @click="handleBulkDelete"> Delete </el-button>
      <el-button v-if="selection.length" @click="isBulkUpdateSparePartsDialogOpen = true"> Update </el-button>
      <el-dropdown>
        <el-button type="primary">
          Add<el-icon class="arrow-icon"><arrow-down /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item @click="isCreationDialogOpen = true">Add manually</el-dropdown-item>
            <el-dropdown-item @click="isBulkAddSparePartsDialogOpen = true">Import</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </template>
    <SparePartsTable @select="onSelect" @select-all="onSelectAll" />
    <AddSparePartsDialog v-model="isCreationDialogOpen" />
    <BulkAddSparePartsDialog v-model="isBulkAddSparePartsDialogOpen" @close="isBulkAddSparePartsDialogOpen = false" />
    <BulkUpdateSparePartsDialog
      v-model="isBulkUpdateSparePartsDialogOpen"
      :spare-parts="selection"
      @close="handleBulkUpdateDialogClose"
    />
  </ViewLayout>
</template>

<style scoped lang="scss">
.el-button {
  margin: 0;
}

.el-dropdown {
  .arrow-icon {
    margin-left: 4px;
    transition: 0.7s cubic-bezier(0.075, 0.82, 0.165, 1);
  }

  &:hover {
    .arrow-icon {
      transform: rotate(-180deg);
    }
  }
}
</style>
